import type { CatalogCard } from "@pokifolio/contracts";
import { Loader2 } from "lucide-react";

import { ErrorAlert } from "../../components/ui/alert";
import { Skeleton } from "../../components/ui/skeleton";
import { serverFieldError } from "../../lib/errors";
import { CardImage } from "./card-image";
import type { useCardLookup } from "./search-queries";

interface CardLookupResultsProps {
  readonly lookup: ReturnType<typeof useCardLookup>;
  readonly busy: boolean;
  /** What choosing a card does, for screen readers, e.g. "Search for Venusaur EX XY28, XY Black Star Promos". */
  readonly actionLabel: (card: CatalogCard) => string;
  readonly onChoose: (card: CatalogCard) => void;
  /** Shown after "No cards found for …". */
  readonly emptyHint: string;
}

/** The cards matching what was typed, with pictures, number and set, so the right one can be chosen. */
export function CardLookupResults({
  lookup,
  busy,
  actionLabel,
  onChoose,
  emptyHint,
}: CardLookupResultsProps) {
  const cards = lookup.enabled ? (lookup.data ?? []) : [];
  const fieldError = lookup.isError ? serverFieldError(lookup.error, "q") : undefined;

  return (
    <div aria-live="polite" className="flex flex-col gap-3">
      {!lookup.enabled ? null : fieldError !== undefined ? (
        <p className="text-sm text-destructive">{fieldError}</p>
      ) : lookup.isError ? (
        <ErrorAlert error={lookup.error} />
      ) : lookup.isPending ? (
        <div className="flex flex-col gap-3" aria-busy="true">
          <p className="inline-flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            Looking up cards…
          </p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" aria-hidden="true">
            {Array.from({ length: 5 }, (_, i) => (
              <div key={i} className="flex flex-col items-center gap-2 rounded-lg border p-2">
                <Skeleton className="aspect-[5/7] w-full" />
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            ))}
          </div>
        </div>
      ) : cards.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No cards found for “{lookup.term}”. {emptyHint}
        </p>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">
            {cards.length === 1 ? "1 card matches." : `${String(cards.length)} cards match.`} Choose the right
            one:
          </p>
          <ul aria-label="Matching cards" className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {cards.map((card) => (
              <li key={card.id}>
                <button
                  type="button"
                  onClick={() => onChoose(card)}
                  disabled={busy}
                  aria-label={actionLabel(card)}
                  className="flex h-full w-full flex-col items-center gap-2 rounded-lg border border-border p-2 text-center transition-colors hover:border-ring hover:bg-accent disabled:cursor-wait"
                >
                  <CardImage src={card.imageSmall} />
                  <span className="text-sm font-medium text-foreground">{card.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {card.number} · {card.setName}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
